/**
 * Authentication Context with Backend Integration
 * Manages user authentication state and API communication
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiConfig, makeApiRequest } from '../services/api';
import { signInWithMicrosoft, clearMicrosoftSession } from '../services/microsoftAuthService';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string; // Allow any role string to support database-driven roles
  department?: string;
  lastLogin?: Date;
  phone?: string;
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    // Read the token before clearing storage - the logout call below needs it.
    const currentToken = localStorage.getItem('authToken');

    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Drop any cached Microsoft account so the next sign-in starts clean.
    clearMicrosoftSession();

    // Call logout endpoint if we have a token
    if (currentToken) {
      fetch(`${apiConfig.baseURL}${apiConfig.endpoints.auth.logout}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      }).catch(console.error);
    }
  }, []);

  /**
   * Persist an authenticated session returned by either sign-in path.
   *
   * Both `/api/auth/login` and `/api/auth/microsoft` return the same
   * `{ user, tokens }` shape, so the two flows share this.
   */
  const applySession = useCallback((payload: any) => {
    const { user: userPayload, tokens } = payload;

    const userData: User = {
      id: userPayload._id,
      email: userPayload.email,
      firstName: userPayload.firstName,
      lastName: userPayload.lastName,
      role: userPayload.role,
      department: userPayload.department,
      lastLogin: userPayload.lastLogin ? new Date(userPayload.lastLogin) : undefined,
      phone: userPayload.phone,
      permissions: userPayload.permissions || []
    };

    setUser(userData);
    setToken(tokens.access);

    localStorage.setItem('authToken', tokens.access);
    localStorage.setItem('refreshToken', tokens.refresh || '');
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  /**
   * Validate token with backend
   */
  const validateToken = useCallback(async (token: string) => {
    try {
      // Always use real backend API for validation
      const response = await makeApiRequest(
        apiConfig.endpoints.auth.me,
        { method: 'GET' },
        token
      );

      if (response.success && response.data) {
        const userData: User = {
          id: response.data._id,
          email: response.data.email,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          role: response.data.role,
          department: response.data.department,
          lastLogin: response.data.lastLogin ? new Date(response.data.lastLogin) : undefined,
          phone: response.data.phone,
          permissions: response.data.permissions || []
        };
        setUser(userData);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }, []);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const savedToken = localStorage.getItem('authToken');
        const savedUser = localStorage.getItem('user');

        if (savedToken && savedUser) {
          setToken(savedToken);
          
          // Validate token with backend
          const isValid = await validateToken(savedToken);
          if (isValid) {
            // User is already set by validateToken
          } else {
            // Token is invalid, clear everything
            logout();
          }
        }
      } catch (error) {
        console.error('Failed to load user:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, [validateToken, logout]);

  /**
   * Login user
   */
  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${apiConfig.baseURL}${apiConfig.endpoints.auth.login}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.data) {
        applySession(data.data);
      } else {
        throw new Error('No user data received from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /**
   * Sign in with Microsoft.
   *
   * Acquires an ID token via MSAL, then exchanges it at the backend for this
   * app's own JWTs. The backend rejects Microsoft identities that have no
   * corresponding account, so a clear message matters more than a generic one.
   */
  const loginWithMicrosoft = async () => {
    const idToken = await signInWithMicrosoft();

    const response = await fetch(
      `${apiConfig.baseURL}${apiConfig.endpoints.auth.microsoftLogin}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ idToken })
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      // Surface the backend's message verbatim - "no account provisioned" and
      // "account deactivated" are both actionable for the user.
      throw new Error(data.message || 'Microsoft sign-in failed.');
    }

    if (!data.data) {
      throw new Error('No user data received from server');
    }

    applySession(data.data);
  };

  /**
   * Refresh authentication token
   */
  const refreshToken = async () => {
    try {
      const savedRefreshToken = localStorage.getItem('refreshToken');
      if (!savedRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${apiConfig.baseURL}${apiConfig.endpoints.auth.refresh}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken: savedRefreshToken })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Token refresh failed');
      }

      if (data.data && data.data.tokens) {
        setToken(data.data.tokens.access);
        localStorage.setItem('authToken', data.data.tokens.access);
        localStorage.setItem('refreshToken', data.data.tokens.refresh);
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      throw error;
    }
  };

  /**
   * Update user information
   */
  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    loginWithMicrosoft,
    logout,
    refreshToken,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};